import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import MdaeDataGrid, {
  IMdaeDataGridProps,
} from "../../common_components/common_widgets/data-grid";
import React from "react";
import MdaeTextBox from "../../common_components/common_widgets/text-box";
import MdaeSelectBox from "../../common_components/common_widgets/select-box/component";
import { callOperation, actions as asyncActions } from "async-ops";
import { isEqual } from "lodash";
import { bindActionCreators } from "redux";
import { loadEventType } from "./constants";
import dxDataGrid from "devextreme/ui/data_grid";
import {
  SET_AUDIT_HISTORY_FILTERS,
  SET_AUDIT_HISTORY_TITLE,
} from "./reducers/external-audit-reducer";
import MdaeButton from "../../common_components/common_widgets/button";
import { GET_AUDIT_PROPERTIES } from "./services/get-audit-properties";
import { GET_EXTERNAL_AUDIT_HISTORY } from "./services/get-external-audit-history";

interface IExternalAuditHistory {
  showInPopup?: boolean;
}

let eaAuditId = 0;

if (window.location.href.toLowerCase().includes("externalaudithistory")) {
  const params = new URLSearchParams(window.location.search);

  if (params.has("auditId")) {
    eaAuditId = parseInt(params.get("auditId") || "0", 10);
  }
}


const ExternalAuditHistory: React.FC<IExternalAuditHistory> =({showInPopup = false}) => {
  const dispatch = useDispatch();
  const [gridInstance, setGridInstance] = useState<dxDataGrid | null>(null);

  const asyncAction = useMemo(
    () =>
      bindActionCreators(
        {
          asyncOperationStart: asyncActions.asyncOperationStart,
        },
        dispatch
      ),
    [dispatch]
  );

  const externalAuditHistoryList = useSelector(
    (state) => state["externalAuditReducer"].externalAuditHistoryList,
    isEqual
  );

  const historyFilters = useSelector(
    (state) => state["externalAuditReducer"].historyFilters,
    isEqual
  );

  const onGridInstance = useCallback((e) => {
    setGridInstance(e.component);
  }, []);

  useEffect(() => {
    if(eaAuditId>0){
      asyncAction.asyncOperationStart(GET_EXTERNAL_AUDIT_HISTORY, {
        eaAuditId: eaAuditId,
        userTaskId:0
      });
    }
  }, [eaAuditId]);

  useEffect(() => {
    if (eaAuditId > 0) {
      callOperation(GET_AUDIT_PROPERTIES, { auditId:eaAuditId }).then((response) => {
        dispatch({
          type: SET_AUDIT_HISTORY_TITLE,
          data: { title: response.auditPropertiesData.title },
        });
      });
    }
  }, [dispatch]);

  const auditHistoryDataGridOptions = useMemo(() => {
    return getExternalAuditHistoryDataGridOptions(
      dispatch,
      externalAuditHistoryList,
      onGridInstance,
      showInPopup
    );
  }, [dispatch, externalAuditHistoryList, onGridInstance,showInPopup]);

  const users = useMemo(
    () => getUniqueSortedUsers(externalAuditHistoryList),
    [externalAuditHistoryList]
  );

  const applyFilter = useCallback(
    (filters) => {
      if (!gridInstance) return;

      const conditions: any[] = [];

      if (filters.caseHash) {
        conditions.push(["targetName", "contains", filters.caseHash]);
      }

      if (filters.accountHash) {
        conditions.push(["targetName", "contains", filters.accountHash]);
      }

      if (filters.selectedUserId) {
        conditions.push(["userId", "=", `${filters.selectedUserId}`]);
      }

      if (filters.eventTypeId) {
        const eventFilter = getEventFilter(filters.eventTypeId);
        if (eventFilter) conditions.push(eventFilter);
      }

      const combinedFilter =
        conditions.length > 1
          ? conditions.reduce((acc, curr) => (acc ? [acc, "and", curr] : curr), null)
          : conditions[0] || null;

      gridInstance.filter(combinedFilter || null);

      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: { isFilterApplied: !!combinedFilter },
      });
    },
    [dispatch, gridInstance]
  );

  const onCaseHashChange = useCallback(() => {
    if (!gridInstance || !historyFilters.caseHash) return;
    applyFilter(historyFilters);
  }, [applyFilter, gridInstance, historyFilters]);

  const onAccountHashChange = useCallback(() => {
    if (!gridInstance || !historyFilters.accountHash) return;
    applyFilter(historyFilters);
  }, [applyFilter, gridInstance, historyFilters]);

  const onUserChange = useCallback(
    (userId) => {
      if (!gridInstance) return;
      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: { selectedUserId: userId },
      });
      applyFilter({ ...historyFilters, selectedUserId: userId });
    },
    [dispatch, applyFilter, gridInstance, historyFilters]
  );

  const onEventTypeChange = useCallback(
    (selectedValue) => {
      if (!gridInstance) return;
      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: { eventTypeId: selectedValue },
      });
      applyFilter({ ...historyFilters, eventTypeId: selectedValue });
    },
    [dispatch, applyFilter, gridInstance, historyFilters]
  );

  const clearFilter = useCallback(() => {
    gridInstance?.clearFilter();
    dispatch({
      type: SET_AUDIT_HISTORY_FILTERS,
      data: {
        eventTypeId: 0,
        selectedUserId: 0,
        caseHash: null,
        accountHash: null,
        isFilterApplied: false,
      },
    });
  }, [dispatch, gridInstance]);

  const renderInputs = useCallback(() => {
    return (
      <div className="row page-container-padding audit-history-search-fields">
        <div
          className="col-xs-12 remove-padding"
          style={{ display: "flex", alignItems: "center" }}
        >
          <MdaeTextBox
            id={"txtCases"}
            mdaeTextBoxTitle="Cases#"
            value={historyFilters.caseHash}
            onValueChange={onCaseHashChangeInput}
          />
          <MdaeButton
            stylingMode="text"
            role="dxButton"
            id="btnManualSearchBillingProvider"
            icon="fa fa-search application-section-primary-color"
            onClick={onCaseHashChange}
          />
        </div>
        <div
          className="col-xs-12 remove-padding"
          style={{ display: "flex", alignItems: "center" }}
        >
          <MdaeTextBox
            id={"txtAccount"}
            mdaeTextBoxTitle="Account#"
            value={historyFilters.accountHash}
            onValueChange={onAccountHashChangeInput}
          />
          <MdaeButton
            stylingMode="text"
            role="dxButton"
            id="btnManualSearchBillingProvider"
            icon="fa fa-search application-section-primary-color"
            onClick={onAccountHashChange}
          />
        </div>
        <div className="col-xs-12 remove-padding" style={{ display: "flex" }}>
          <MdaeSelectBox
            id="txtUserName"
            mdaeDropDownBoxTitle="User Name"
            dataSource={users}
            displayExpr="userName"
            valueExpr="userId"
            value={historyFilters.selectedUserId}
            onValueChange={onUserChange}
          />
        </div>
        <div className="col-xs-12 remove-padding" style={{ display: "flex" }}>
          <MdaeSelectBox
            id={"txtEventName"}
            mdaeDropDownBoxTitle="Event Type"
            dataSource={loadEventType}
            displayExpr={"eventTypeName"}
            valueExpr={"eventTypeId"}
            onValueChange={onEventTypeChange}
            value={historyFilters.eventTypeId}
          />
        </div>
        <div
          id="clearFilterButton"
          onClick={clearFilter}
          className="filter-icon"
          title="Clear Filter"
          aria-label="Clear Filter"
          role="button"
          style={{
            color: historyFilters.isFilterApplied
              ? "var(--application-theme-color)"
              : "darkgray",
            paddingTop: "10px",
          }}
        >
          <span className="fa-stack" style={{ fontSize: "11px" }}>
            <i className="fa fa-filter fa-stack-2x "></i>
            <i
              className="fa fa-times fa-stack fa-times-style "
              style={{ position: "absolute", left: "65%" }}
            ></i>
          </span>
        </div>
      </div>
    );
  }, [
    historyFilters,
    onCaseHashChange,
    onAccountHashChange,
    onUserChange,
    onEventTypeChange,
    clearFilter,
    users,
  ]);

  const onCaseHashChangeInput = useCallback(
    (value: string) => {
      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: { caseHash: value },
      });
    },
    [dispatch]
  );

  const onAccountHashChangeInput = useCallback(
    (value: string) => {
      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: { accountHash: value },
      });
    },
    [dispatch]
  );

  return (
    <div className={"external-audit-history-container"} style={{ padding: 20 }}>
      {!showInPopup && renderInputs()}
      <MdaeDataGrid {...auditHistoryDataGridOptions} />
    </div>
  );
};

function getUniqueSortedUsers(auditReviewHistory: any[]) {
  if (!Array.isArray(auditReviewHistory)) return [];

  const uniqueUsersMap = new Map();
  auditReviewHistory.forEach((user) => {
    if (user?.userName && user.userId != null) {
      uniqueUsersMap.set(user.userId, user);
    }
  });

  return Array.from(uniqueUsersMap.values()).sort((a, b) =>
    a.userName.localeCompare(b.userName)
  );
}

function getEventFilter(selectedValue: number) {
  switch (selectedValue) {
    case 1:
      return [
        ["auditEventName", "=", "EA_AUDIT_PROPERTIES_FIELD_UPDATE"],
        "or",
        ["auditEventName", "=", "EA_AUDIT_CASE_ADD"],
        "or",
        ["auditEventName", "=", "EA_AUDIT_CASE_REMOVED"],
        "or",
        ["auditEventName", "=", "EA_AUDIT_CASE_RESTORE"],
        "or",
        ["auditEventName", "=", "EA_AUDIT_CREATE"],
        "or",
        ["auditEventName", "=", "EA_AUDIT_DELETE"],
      ];
    case 2:
      return ["auditEventName", "=", "EA_AUDIT_CASE_PROPERTIES_UPDATE"];
    case 3:
      return ["auditEventName", "=", "EA_AUDIT_CASE_ROUND_PROPERTIES_UPDATE"];
    case 4:
      return [
        ["auditEventName", "=", "TASK_UPDATE"],
        "or",
        ["auditEventName", "=", "TASK_DELETE"],
        "or",
        ["auditEventName", "=", "TASK_CREATE_MANUAL"],
        "or",
        ["auditEventName", "=", "TASK_CREATE_SYSTEM"],
      ];
    case 5:
      return [
        ["auditEventName", "=", "EA_DOCUMENT_UPLOAD"],
        "or",
        ["auditEventName", "=", "EA_DOCUMENT_DOWNLOAD"],
        "or",
        ["auditEventName", "=", "EAC_DOCUMENT_UPLOAD"],
        "or",
        ["auditEventName", "=", "EAC_DOCUMENT_DOWNLOAD"],
        "or",
        ["auditEventName", "=", "TS_DOCUMENT_UPLOAD"],
        "or",
        ["auditEventName", "=", "TS_DOCUMENT_DOWNLOAD"],
      ];
    default:
      return [];
  }
}

const getExternalAuditHistoryDataGridOptions = (
  dispatch,
  externalAuditHistoryList,
  onGridInstance,
  showInPopup
) => {
  const getEventDescription = (rowData) => {
    let desc = rowData.eventAction;

    if (desc === "Splitted") {
      return rowData.targetName;
    }

    if (rowData.eventQnttyView) desc += " " + rowData.eventQnttyView;
    if (rowData.referenceName )
      desc += " " + rowData.referenceName;
    if (rowData.targetPrefix && rowData.auditEventName !== "EA_AUDIT_CASE_RESTORE")
      desc += " " + rowData.targetPrefix;
    if (rowData.targetName) desc += " " + rowData.targetName;
    if (rowData.previousValue) desc += ` from ${rowData.previousValue}`;
    if (rowData.eventValuePrefix ){
      if(rowData.eventValue){
        desc += " " + rowData.eventValuePrefix + ` ${rowData.eventValue}`;
      }
      else{
        desc += " " + rowData.eventValuePrefix + ` " "`;
      }
    }
    else if(rowData.eventValue){
      desc += " " +` ${rowData.eventValue}`;
    }
    if (rowData.eventValueSuffix ){
     
        desc += "  " + rowData.eventValueSuffix ;
      
    }
    return desc;
  };

  const auditHistoryDataGridOptions: IMdaeDataGridProps = {
    id: "pnlExternalAuditHistoryListGrid",
    mdaeColumnChooser: { isMdaeColumnChooserVisible: true },
    dataSource: externalAuditHistoryList,
    columnAutoWidth: true,
    allowColumnResizing: true,
    allowColumnReordering: true,
    columnChooser: { enabled: false },
    onInitialized: onGridInstance,
    paging: { enabled: true },
    mdaeStateStoring: {
      enabled: true,
      storageKey: "auditHistoryDataGridStoragekey",
    },
    clearFilterButtonId: "btn-audit-history-clear-filter",
    filterRow: { visible: true },
    onDataGridClearFilterClick() {
      dispatch({
        type: SET_AUDIT_HISTORY_FILTERS,
        data: {
          eventTypeId: 0,
          selectedUserId: 0,
          caseHash: null,
          accountHash: null,
          isFilterApplied: false,
        },
      });
    },
    export: { enabled: true, fileName:showInPopup? "History List": "Audit History" },
    width: "100%",
    headerFilter: { visible: true },
    mdaeColumns: [
      {
        caption: "Date",
        dataField: "eventDate",
        dataType: "date",
        format: "MM/dd/yyyy HH:mm",
        allowFiltering: false,
        allowSorting: true,
        width: 170,
      },
      {
        caption: "User Name",
        dataField: "userNameView",
        width: 250,
        allowFiltering: true,
        allowReordering: true,
        allowSearch: true,
        allowResizing: true,
      },
      {
        caption: "Event Type",
        dataField: "eventAction",
        width: 180,
        allowFiltering: true,
        allowReordering: true,
        allowSearch: true,
        allowResizing: true,
        dataType: "string",
        calculateCellValue: getEventDescription,
      },
    ],
  };
  return auditHistoryDataGridOptions;
};

export default ExternalAuditHistory;
